import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import styled, { keyframes } from "styled-components";
import {
  adminGetUXOverview,
  adminGetUXFunnel,
  adminGetUXClicks,
  adminGetUXActivation,
  adminGetUXEngagement,
  adminGetUXCohorts,
  adminGetUXErrors,
  adminGetUXLatency,
  adminGetUXDevices,
  adminGetUXBehavioral,
  adminGetUXVolume,
  adminGetUXByCountry,
} from "./adminClient";
import WorldSessionsMap from "./WorldSessionsMap";

/* ═══════════════════════════════════════════════════════════════════════════
   UX ANALYTICS DASHBOARD — EVPTrading— WIRED TO API
   ─────────────────────────────────────────────────────────────────────────
   Chaque panneau charge ses données en parallèle. En cas d'échec ou de
   données vides (tracking pas encore déployé), on bascule sur des MOCK
   réalistes par panneau, pour que le dashboard ne soit jamais "cassé".
═══════════════════════════════════════════════════════════════════════════ */

/* ─── Animations ─────────────────────────────────────────────────────────── */
const fadeUp  = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;
const shimmer = keyframes`0%{background-position:-400px 0}100%{background-position:400px 0}`;
const grow    = keyframes`from{transform:scaleX(0)}to{transform:scaleX(1)}`;

/* ─── Layout ─────────────────────────────────────────────────────────────── */
const Wrap = styled.div`max-width:1280px;margin:0 auto;padding:2rem 2rem 4rem;@media(max-width:740px){padding:1.5rem 1rem 3rem;}`;
const Head = styled.div`display:flex;align-items:flex-end;justify-content:space-between;margin-bottom:2rem;flex-wrap:wrap;gap:1rem;`;
const Title = styled.h1`font-family:${({theme})=>theme.fontDisplay};font-size:1.8rem;font-weight:700;letter-spacing:-.03em;span{color:${({theme})=>theme.accent};}`;
const Sub = styled.p`font-size:.78rem;color:${({theme})=>theme.textMuted};margin-top:.25rem;`;
const RangeTabs = styled.div`display:flex;gap:.3rem;`;
const RangeBtn = styled.button`
  font-family:${({theme})=>theme.fontMono};font-size:.65rem;
  padding:.35rem .8rem;border-radius:100px;letter-spacing:.05em;
  border:1px solid ${({$a,theme})=>$a?theme.borderLight:theme.border};
  background:${({$a,theme})=>$a?theme.bgHover:"transparent"};
  color:${({$a,theme})=>$a?theme.text:theme.textMuted};
  cursor:pointer;transition:all .15s;
  &:hover{border-color:${({theme})=>theme.borderLight};}
`;
const FieldMockBadge = styled.span`
  font-family: ${({theme}) => theme.fontMono};
  font-size: .55rem;
  color: ${({theme}) => theme.gold};
  background: rgba(255,193,7,.08);
  border: 1px solid ${({theme}) => theme.gold}33;
  border-radius: 4px;
  padding: .08rem .4rem;
  margin-left: .4rem;
  letter-spacing: .04em;
  text-transform: uppercase;
`;
const RefreshBtn = styled.button`
  font-family:${({theme})=>theme.fontMono};font-size:.65rem;
  padding:.35rem .7rem;border-radius:7px;letter-spacing:.05em;
  border:1px solid ${({theme})=>theme.border};background:transparent;
  color:${({theme})=>theme.textMuted};cursor:pointer;transition:all .15s;
  &:hover{color:${({theme})=>theme.text};border-color:${({theme})=>theme.borderLight};}
`;

/* ─── KPI strip ──────────────────────────────────────────────────────────── */
const KpiGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:.75rem;margin-bottom:1.75rem;`;
const Kpi = styled.div`
  background:${({theme})=>theme.bgCard};border:1px solid ${({theme})=>theme.border};
  border-radius:12px;padding:1rem 1.1rem;animation:${fadeUp} .3s ease both;
  animation-delay:${({$i})=>($i||0)*.05}s;
  border-left:3px solid ${({$c,theme})=>$c==="green"?theme.green:$c==="accent"?theme.accent:$c==="gold"?theme.gold:$c==="red"?theme.red:theme.border};
`;
const KpiLabel = styled.div`font-family:${({theme})=>theme.fontMono};font-size:.6rem;letter-spacing:.08em;text-transform:uppercase;color:${({theme})=>theme.textMuted};margin-bottom:.4rem;display:flex;align-items:center;gap:.3rem;`;
const KpiVal = styled.div`font-family:${({theme})=>theme.fontDisplay};font-size:1.55rem;font-weight:700;letter-spacing:-.02em;line-height:1;`;
const KpiSub = styled.div`font-family:${({theme})=>theme.fontMono};font-size:.65rem;margin-top:.35rem;color:${({$ok,theme})=>$ok?theme.green:theme.textMuted};`;

/* ─── Panel ──────────────────────────────────────────────────────────────── */
const Row2 = styled.div`display:grid;grid-template-columns:repeat(auto-fit,minmax(380px,1fr));gap:1rem;margin-bottom:1.25rem;`;
const Panel = styled.div`
  background:${({theme})=>theme.bgCard};border:1px solid ${({theme})=>theme.border};
  border-radius:12px;padding:1.1rem 1.25rem;animation:${fadeUp} .35s ease both;position:relative;
`;
const PanelHead = styled.div`display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:1rem;gap:1rem;`;
const PanelTitle = styled.h2`font-size:.95rem;font-weight:600;letter-spacing:-.01em;`;
const PanelHint = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.6rem;color:${({theme})=>theme.textMuted};letter-spacing:.05em;`;
const PanelSkel = styled.div`
  height:${({$h})=>$h||"180px"};border-radius:8px;
  background:linear-gradient(90deg,${({theme})=>theme.bg} 25%,${({theme})=>theme.bgHover} 50%,${({theme})=>theme.bg} 75%);
  background-size:400px 100%;animation:${shimmer} 1.4s infinite linear;
`;
const MockTag = styled.span`
  position:absolute;top:.8rem;right:.8rem;
  font-family:${({theme})=>theme.fontMono};font-size:.55rem;
  background:rgba(255,193,7,.12);color:${({theme})=>theme.gold};
  border:1px solid ${({theme})=>theme.gold}33;border-radius:100px;
  padding:.1rem .45rem;letter-spacing:.05em;text-transform:uppercase;
`;
const Insight = styled.div`
  display:flex;gap:.5rem;align-items:flex-start;
  background:${({$c,theme})=>$c==="red"?"rgba(255,59,92,.08)":$c==="gold"?"rgba(255,193,7,.08)":"rgba(79,195,247,.08)"};
  border:1px solid ${({$c,theme})=>$c==="red"?theme.red+"33":$c==="gold"?theme.gold+"33":theme.accent+"33"};
  color:${({$c,theme})=>$c==="red"?theme.red:$c==="gold"?theme.gold:theme.accent};
  border-radius:8px;padding:.55rem .75rem;font-size:.72rem;margin-top:.85rem;line-height:1.5;
  strong{font-weight:700;}
`;

/* ─── Funnel ─────────────────────────────────────────────────────────────── */
const FunRow = styled.div`display:grid;grid-template-columns:140px 1fr 90px 60px;gap:.75rem;align-items:center;margin-bottom:.55rem;font-size:.78rem;`;
const FunLabel = styled.span`color:${({theme})=>theme.textDim};`;
const FunTrack = styled.div`background:${({theme})=>theme.bg};border-radius:6px;height:24px;overflow:hidden;position:relative;`;
const FunFill = styled.div`
  height:100%;width:${({$pct})=>$pct}%;
  background:linear-gradient(90deg,${({$c,theme})=>$c==="red"?theme.red:$c==="gold"?theme.gold:theme.green},${({$c,theme})=>$c==="red"?theme.red:$c==="gold"?theme.gold:theme.green}88);
  border-radius:6px;transform-origin:left;animation:${grow} .8s cubic-bezier(.4,0,.2,1) both;
`;
const FunNum = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.72rem;color:${({theme})=>theme.text};text-align:right;`;
const FunPct = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.7rem;color:${({$c,theme})=>$c==="red"?theme.red:$c==="gold"?theme.gold:theme.green};text-align:right;font-weight:600;`;

/* ─── Bars list ──────────────────────────────────────────────────────────── */
const BarItem = styled.div`display:flex;align-items:center;gap:.6rem;margin-bottom:.55rem;font-size:.74rem;`;
const BarIcon = styled.span`width:18px;flex-shrink:0;text-align:center;font-size:.85rem;`;
const BarLabel = styled.span`width:130px;flex-shrink:0;color:${({theme})=>theme.textDim};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`;
const BarTrack = styled.div`flex:1;background:${({theme})=>theme.bg};border-radius:4px;height:8px;overflow:hidden;`;
const BarFill = styled.div`
  height:100%;width:${({$pct})=>$pct}%;
  background:${({$c,theme})=>$c==="red"?theme.red:$c==="gold"?theme.gold:$c==="accent"?theme.accent:theme.green};
  border-radius:4px;transform-origin:left;animation:${grow} .7s cubic-bezier(.4,0,.2,1) both;
`;
const BarVal = styled.span`width:55px;text-align:right;font-family:${({theme})=>theme.fontMono};font-size:.7rem;color:${({theme})=>theme.text};`;

/* ─── Cohort heatmap ─────────────────────────────────────────────────────── */
const CohortTable = styled.table`width:100%;border-collapse:separate;border-spacing:3px;font-size:.7rem;`;
const CohortHead = styled.th`font-family:${({theme})=>theme.fontMono};font-size:.6rem;color:${({theme})=>theme.textMuted};font-weight:500;text-align:center;padding:.2rem;text-transform:uppercase;`;
const CohortLabel = styled.td`font-family:${({theme})=>theme.fontMono};font-size:.62rem;color:${({theme})=>theme.textMuted};text-align:left;padding:.2rem .4rem;`;
const CohortCell = styled.td`
  text-align:center;padding:.3rem;border-radius:4px;
  font-family:${({theme})=>theme.fontMono};font-variant-numeric:tabular-nums;
  background:${({$v}) => {
    if ($v === null || $v === undefined) return "transparent";
    const alpha = Math.min(0.85, 0.1 + $v / 100 * 0.8);
    return `rgba(79,195,247,${alpha})`;
  }};
  color:${({$v}) => $v == null ? "transparent" : $v > 50 ? "#000" : "#e8edf2"};
`;

/* ─── Funnel evolution mini-cards ────────────────────────────────────────── */
const EvolGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: .75rem;
  margin-top: 1rem;
`;
const EvolCard = styled.div`
  background: ${({theme}) => theme.bg};
  border: 1px solid ${({theme}) => theme.border};
  border-radius: 8px;
  padding: .75rem .85rem;
  display: flex; flex-direction: column; gap: .4rem;
  animation: ${fadeUp} .35s ease both;
  animation-delay: ${({$i}) => ($i || 0) * .05}s;
  border-left: 2px solid ${({$c, theme}) =>
    $c === "green" ? theme.green :
    $c === "gold"  ? theme.gold :
    $c === "red"   ? theme.red :
                     theme.accent};
`;
const EvolHead = styled.div`
  display: flex; justify-content: space-between; align-items: flex-start;
  gap: .4rem;
`;
const EvolLabel = styled.span`
  font-family: ${({theme}) => theme.fontMono};
  font-size: .58rem; letter-spacing: .06em; text-transform: uppercase;
  color: ${({theme}) => theme.textMuted};
  line-height: 1.2;
`;
const EvolValue = styled.span`
  font-family: ${({theme}) => theme.fontMono};
  font-size: 1.05rem; font-weight: 700;
  color: ${({theme}) => theme.text};
  letter-spacing: -.01em;
`;
const EvolDelta = styled.span`
  font-family: ${({theme}) => theme.fontMono};
  font-size: .62rem; font-weight: 600;
  color: ${({$up, theme}) => $up ? theme.green : theme.red};
  display: inline-flex; align-items: center; gap: .1rem;
  white-space: nowrap;
`;

/* ─── Histogram ──────────────────────────────────────────────────────────── */
const HistoWrap = styled.div`display:flex;align-items:flex-end;gap:.55rem;height:140px;padding-top:1rem;`;
const HistoBar = styled.div`flex:1;display:flex;flex-direction:column;align-items:center;gap:.4rem;`;
const HistoFill = styled.div`
  width:100%;height:${({$h})=>$h}%;min-height:6px;
  background:${({$c,theme})=>$c==="warn"?theme.gold:theme.accent};opacity:.85;
  border-radius:4px 4px 0 0;animation:${grow} .6s ease both;transform-origin:bottom;
`;
const HistoLab = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.56rem;color:${({theme})=>theme.textMuted};text-align:center;letter-spacing:.04em;`;
const HistoVal = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.62rem;color:${({theme})=>theme.text};font-weight:600;`;

/* ─── Sparkline ──────────────────────────────────────────────────────────── */
const SparkSvg = styled.svg`width:100%;height:160px;`;

/* ─── Donut ──────────────────────────────────────────────────────────────── */
const DonutWrap = styled.div`display:flex;align-items:center;gap:1.25rem;`;
const DonutSvg = styled.svg`width:120px;height:120px;flex-shrink:0;`;
const LegendList = styled.div`display:flex;flex-direction:column;gap:.55rem;flex:1;`;
const LegItem = styled.div`display:flex;align-items:center;gap:.5rem;font-size:.74rem;color:${({theme})=>theme.textDim};`;
const LegDot = styled.span`width:10px;height:10px;border-radius:2px;background:${({$c})=>$c};`;
const LegPct = styled.span`margin-left:auto;font-family:${({theme})=>theme.fontMono};color:${({theme})=>theme.text};font-weight:600;`;

/* ─── Stat list ──────────────────────────────────────────────────────────── */
const StatList = styled.div`display:flex;flex-direction:column;`;
const StatRow = styled.div`
  display:flex;justify-content:space-between;align-items:baseline;
  padding:.55rem 0;border-bottom:1px solid ${({theme})=>theme.border};
  &:last-child{border-bottom:none;}
`;
const StatLbl = styled.span`font-size:.72rem;color:${({theme})=>theme.textMuted};`;
const StatVl = styled.span`
  font-family:${({theme})=>theme.fontMono};font-size:.85rem;font-weight:600;
  color:${({$c,theme})=>$c==="good"?theme.green:$c==="bad"?theme.red:$c==="warn"?theme.gold:theme.text};
`;

/* ═══════════════════════════════════════════════════════════════════════════
   MOCKS PAR PANNEAU — utilisés si l'API échoue ou retourne vide
═══════════════════════════════════════════════════════════════════════════ */
const MOCK_OVERVIEW = {
  stickiness:27, ttft_median_min:14, signup_to_trade:21, latency_p95_ms:428, sessions_today:1410,
};
// Générateur de série journalière réaliste (croissance + bruit)
const generateDailySeries = (total, days = 30, growth = 1.0) => {
  const base = total / days;
  return Array.from({length: days}, (_, i) => {
    const trend = base * (1 + (i / days) * (growth - 1));
    const noise = (Math.random() - 0.5) * base * 0.3;
    const weekend = (i % 7 === 5 || i % 7 === 6) ? 0.8 : 1;  // -20% le weekend
    return Math.max(0, Math.round((trend + noise) * weekend));
  });
};

const MOCK_FUNNEL = { steps:[
  { step:"Visites du site",  n:10000, from:null,  conversion:100, daily: generateDailySeries(10000, 30, 1.3) },
  { step:"Inscriptions",      n:4200,  from:10000, conversion:42,  daily: generateDailySeries(4200,  30, 1.4) },
  { step:"Email vérifié",     n:3780,  from:4200,  conversion:90,  daily: generateDailySeries(3780,  30, 1.4) },
  { step:"KYC complété",      n:2680,  from:3780,  conversion:71,  daily: generateDailySeries(2680,  30, 1.5) },
  { step:"1er dépôt",         n:2280,  from:2680,  conversion:85,  daily: generateDailySeries(2280,  30, 1.6) },
  { step:"1er trade",         n:2100,  from:2280,  conversion:92,  daily: generateDailySeries(2100,  30, 1.7) },
]};
const MOCK_CLICKS = {
  by_page:[
    { page:"MarketDetail",    clicks:48230, bounce:18 },
    { page:"MarketList",      clicks:32100, bounce:12 },
    { page:"WalletPage",      clicks:14580, bounce:32 },
    { page:"PositionList",    clicks:8920,  bounce:22 },
    { page:"MyTickets",       clicks:3210,  bounce:41 },
    { page:"AccountSettings", clicks:2840,  bounce:28 },
    { page:"HowItWorks",      clicks:2150,  bounce:67 },
  ],
  top_elements:[
    { element:"trade_confirm_buy",  count:28940 },
    { element:"outcome_card",       count:22150 },
    { element:"trade_confirm_sell", count:9830  },
    { element:"deposit_btn",        count:6210  },
    { element:"chart_range_picker", count:4830  },
    { element:"category_filter",    count:3450  },
    { element:"login_btn",          count:2890  },
    { element:"history_tab",        count:980   },
  ],
};
const MOCK_ACTIVATION = {
  ttft:[
    { bucket:"<5 min",    pct:35, count:735 },
    { bucket:"5-30 min",  pct:25, count:525 },
    { bucket:"30 m-1 j",  pct:20, count:420 },
    { bucket:"1-3 jours", pct:10, count:210 },
    { bucket:">3 jours",  pct:10, count:210 },
  ],
  trade_size:[
    { bucket:"1-10",   pct:22, count:1830 },
    { bucket:"10-50",  pct:38, count:3160 },
    { bucket:"50-200", pct:25, count:2080 },
    { bucket:"200-1k", pct:12, count:1000 },
    { bucket:">1k",    pct:3,  count:250  },
  ],
};
const MOCK_ENGAGEMENT = {
  days: Array.from({length:30},(_,i)=>i+1),
  dau:  [820,840,870,900,1050,910,830,860,920,980,1100,1080,950,890,920,1010,1180,1240,1110,980,940,1020,1190,1310,1280,1150,1080,1190,1340,1410],
  mau:  [3500,3520,3540,3580,3620,3660,3700,3740,3800,3870,3940,4010,4080,4140,4200,4260,4320,4380,4450,4510,4570,4620,4680,4740,4790,4830,4870,4910,4960,5010],
};
const MOCK_COHORTS = { cohorts:[
  { cohort:"Sem. 1", values:[100,72,55,42,35,30,28,26] },
  { cohort:"Sem. 2", values:[100,68,52,40,33,29,26,null] },
  { cohort:"Sem. 3", values:[100,75,58,44,36,31,null,null] },
  { cohort:"Sem. 4", values:[100,70,54,41,34,null,null,null] },
  { cohort:"Sem. 5", values:[100,73,57,43,null,null,null,null] },
  { cohort:"Sem. 6", values:[100,78,61,null,null,null,null,null] },
  { cohort:"Sem. 7", values:[100,74,null,null,null,null,null,null] },
  { cohort:"Sem. 8", values:[100,null,null,null,null,null,null,null] },
]};
const MOCK_ERRORS = { errors:[
  { label:"insufficient_balance",  count:450, pct:45 },
  { label:"invalid_amount",        count:220, pct:22 },
  { label:"market_closed",         count:150, pct:15 },
  { label:"session_expired",       count:120, pct:12 },
  { label:"network_error",         count:60,  pct:6  },
]};
const MOCK_LATENCY = {
  days: Array.from({length:14},(_,i)=>i+1),
  p50:  [145,150,148,142,160,155,148,140,135,820,290,180,165,158],
  p95:  [380,395,410,420,440,455,430,400,388,1850,720,510,468,428],
};
const MOCK_DEVICES = { devices:[
  { name:"Mobile",  pct:58, c:"#4fc3f7" },
  { name:"Desktop", pct:36, c:"#00e676" },
  { name:"Tablet",  pct:6,  c:"#ffc107" },
]};
const MOCK_BEHAVIORAL = { items:[
  { lbl:"Consultent l'historique avant de miser",    val:"38%",       c:"bad"  },
  { lbl:"Font un 2e trade dans les 24h",             val:"67%",       c:"good" },
  { lbl:"Confirment après ouverture du TradeModal",  val:"71%",       c:"warn" },
  { lbl:"Latence Mobile / Desktop (médiane)",        val:"280/150 ms",c:"warn" },
  { lbl:"Tickets support / 100 trades",              val:"2.3",       c:"good" },
  { lbl:"Délai dépôt → 1er trade (médiane)",         val:"38 min",    c:"warn" },
]};

const MOCK_VOLUME = {
  days: Array.from({length: 30}, (_, i) => i + 1),
  labels: Array.from({length: 30}, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (29 - i));
    return d.toISOString().slice(0, 10);
  }),
  volume: [
    820, 1240, 940, 1580, 2100, 1820, 1450, 1670, 2240, 2890,
    1980, 2120, 3450, 2780, 2540, 3120, 4280, 3890, 3220, 2980,
    3450, 4120, 5230, 4780, 4120, 4560, 5890, 6780, 6240, 7120,
  ],
  trades: [
    12, 18, 14, 22, 28, 24, 19, 21, 28, 35,
    26, 27, 42, 35, 31, 38, 51, 47, 39, 36,
    42, 49, 62, 57, 49, 54, 68, 79, 73, 84,
  ],
  total_volume: 96320,
  total_trades: 1184,
  avg_per_day:  3210.7,
};
 

 
// ─── MODIF 2 — Ajouter le mock à côté des autres MOCK_xxx ────────────────
//
// Tu peux le coller juste après `MOCK_VOLUME`.
 
const MOCK_BY_COUNTRY = {
  countries: [
    { code: "FR", name: "France",         flag: "🇫🇷", users_count: 184, total_volume: 28940, trades_count: 1240 },
    { code: "CI", name: "Côte d'Ivoire",  flag: "🇨🇮", users_count: 142, total_volume: 18650, trades_count: 890  },
    { code: "SN", name: "Sénégal",        flag: "🇸🇳", users_count: 98,  total_volume: 12380, trades_count: 620  },
    { code: "CM", name: "Cameroun",       flag: "🇨🇲", users_count: 76,  total_volume: 9420,  trades_count: 480  },
    { code: "MA", name: "Maroc",          flag: "🇲🇦", users_count: 64,  total_volume: 8210,  trades_count: 410  },
    { code: "BJ", name: "Bénin",          flag: "🇧🇯", users_count: 52,  total_volume: 6180,  trades_count: 310  },
    { code: "TG", name: "Togo",           flag: "🇹🇬", users_count: 41,  total_volume: 4920,  trades_count: 245  },
    { code: "BE", name: "Belgique",       flag: "🇧🇪", users_count: 38,  total_volume: 5840,  trades_count: 220  },
    { code: "DZ", name: "Algérie",        flag: "🇩🇿", users_count: 31,  total_volume: 3760,  trades_count: 175  },
    { code: "CH", name: "Suisse",         flag: "🇨🇭", users_count: 24,  total_volume: 4280,  trades_count: 145  },
    { code: "CA", name: "Canada",         flag: "🇨🇦", users_count: 19,  total_volume: 2890,  trades_count: 120  },
    { code: "??", name: "Inconnu",        flag: "🌐", users_count: 28,  total_volume: 1450,  trades_count: 88   },
  ],
  total_users:   797,
  total_volume:  106920,
  total_trades:  4943,
  country_count: 12,
};

const ICON_BY_PAGE = {
  MarketDetail:"📊", MarketList:"📈", WalletPage:"💰",
  PositionList:"📂", MyTickets:"🎫", AccountSettings:"⚙",
  HowItWorks:"📖", AdminDashboard:"🔑",
};
const ICON_BY_ELEMENT = (el="") => {
  if (/buy/i.test(el))      return "🟢";
  if (/sell/i.test(el))     return "🔴";
  if (/deposit/i.test(el))  return "💵";
  if (/login/i.test(el))    return "🔐";
  if (/outcome/i.test(el))  return "💎";
  if (/history/i.test(el))  return "📜";
  if (/category/i.test(el)) return "🔍";
  return "•";
};
const COLOR_BY_ELEMENT = (el="") => {
  if (/buy/i.test(el) && !/sell/i.test(el)) return "green";
  if (/sell/i.test(el)) return "red";
  if (/history|login/i.test(el)) return "gold";
  return "accent";
};

const RANGES = [
  { val:"7d",  label:"7 J" },
  { val:"30d", label:"30 J" },
  { val:"90d", label:"90 J" },
  { val:"all", label:"Tout" },
];

/* ─── Helpers ────────────────────────────────────────────────────────────── */
const fmtNum = (n) => {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n/1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n/1_000).toFixed(1)}K`;
  return n.toLocaleString("fr-FR");
};
const conversionColor = (p) => p >= 80 ? "green" : p >= 50 ? "gold" : "red";

/**
 * Custom hook : load + auto-fallback to mock if API fails or returns empty.
 * Returns { data, loading, isMock, reload }.
 */
function useApiSection(fetcher, mockData, deps = []) {
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMock, setIsMock]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetcher();
      const payload = res.data;
      // Heuristic emptiness check : if payload is "empty enough", use mock
      if (isEmpty(payload)) {
        setData(mockData);
        setIsMock(true);
      } else {
        setData(payload);
        setIsMock(false);
      }
    } catch {
      setData(mockData);
      setIsMock(true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => { load(); }, [load]);
  return { data, loading, isMock, reload: load };
}

/**
 * Merge real payload + mock : pour chaque champ tableau du mock, si le
 * tableau de la payload est vide/absent → utilise le mock pour ce champ.
 * Renvoie aussi un objet `missing` indiquant quels champs sont issus du mock.
 */
function mergeWithMock(payload, mock) {
  if (!payload) return { data: mock, missing: Object.keys(mock) };

  const data = { ...payload };
  const missing = [];

  for (const key of Object.keys(mock)) {
    const value = payload[key];
    const isArrayEmpty = Array.isArray(value) && value.length === 0;
    const isAbsent     = value === undefined || value === null;

    if (isArrayEmpty || isAbsent) {
      data[key] = mock[key];
      missing.push(key);
    }
  }

  return { data, missing };
}

function isEmpty(payload) {
  if (!payload) return true;
  // Steps/items/devices/errors arrays empty
  if (Array.isArray(payload)) return payload.length === 0;
  // {steps:[]}, {items:[]}, etc.
  const arrayKeys = ["steps","items","cohorts","devices","errors","by_page","top_elements","ttft","trade_size"];
  for (const k of arrayKeys) {
    if (Array.isArray(payload[k]) && payload[k].length > 0) return false;
  }
  // KPI overview check — if all zeros, treat as empty
  if ("stickiness" in payload) {
    const sum = (payload.stickiness||0) + (payload.ttft_median_min||0) + (payload.signup_to_trade||0) + (payload.latency_p95_ms||0);
    return sum === 0;
  }
  // Latency / engagement — check series
  if (Array.isArray(payload.dau) && payload.dau.some(v => v > 0)) return false;
  if (Array.isArray(payload.p50) && payload.p50.some(v => v > 0)) return false;
  return false;  // permissive: show whatever the API gave us
}

/* ═══════════════════════════════════════════════════════════════════════════
   SUB-COMPONENTS — charts
═══════════════════════════════════════════════════════════════════════════ */

 
function Sparkline({ days, series, labels = null }) {
  const W = 600;
  const H = 180;
  const PAD = { t: 12, r: 12, b: 26, l: 38 };
 
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null);
  // hover = null OU { idx: 5, x: 142, valuesAtIdx: [...] }
 
  const allVals = series.flatMap(s => s.data).filter(v => v != null);
  const maxV = Math.max(...allVals, 1) * 1.1;
 
  const xScale = (i) =>
    PAD.l + (i / Math.max(days.length - 1, 1)) * (W - PAD.l - PAD.r);
  const yScale = (v) =>
    PAD.t + (1 - v / maxV) * (H - PAD.t - PAD.b);
 
  const path = (data) =>
    data
      .map((v, i) => `${i === 0 ? "M" : "L"} ${xScale(i)} ${yScale(v ?? 0)}`)
      .join(" ");
 
  // Convertit une position X souris → index du jour le plus proche
  const handleMouseMove = (e) => {
    if (!svgRef.current || !days.length) return;
    const rect = svgRef.current.getBoundingClientRect();
    // Mouse X dans le repère du viewBox (W = 600 logique)
    const ratio = W / rect.width;
    const mouseX = (e.clientX - rect.left) * ratio;
    // Limiter à la zone de tracé
    const innerW = W - PAD.l - PAD.r;
    const t = Math.max(0, Math.min(1, (mouseX - PAD.l) / innerW));
    const idx = Math.round(t * (days.length - 1));
    setHover({ idx });
  };
 
  const handleMouseLeave = () => setHover(null);
 
  // Format de date FR pour le tooltip
  const formatLabel = (idx) => {
    if (labels && labels[idx]) {
      try {
        const d = new Date(labels[idx]);
        return d.toLocaleDateString("fr-FR", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
      } catch {
        return labels[idx];
      }
    }
    return `Jour ${days[idx] ?? idx + 1}`;
  };
 
  // Format des valeurs (réutilise fmtNum si dispo dans le scope)
  const fmt = (v) => {
    if (v == null) return "—";
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
    if (Number.isInteger(v)) return v.toLocaleString("fr-FR");
    return v.toFixed(2);
  };
 
  return (
    <div style={{ position: "relative" }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height: "180px", display: "block", cursor: "crosshair" }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* ── Grille horizontale + labels Y ── */}
        {[0, 0.5, 1].map((t, i) => {
          const y = PAD.t + (1 - t) * (H - PAD.t - PAD.b);
          return (
            <g key={i}>
              <line
                x1={PAD.l}
                y1={y}
                x2={W - PAD.r}
                y2={y}
                stroke="#1a2530"
                strokeWidth="0.5"
              />
              <text
                x={PAD.l - 6}
                y={y + 3}
                textAnchor="end"
                fontSize="9"
                fontFamily="DM Mono,monospace"
                fill="#5a6a7a"
              >
                {fmt(Math.round(maxV * t))}
              </text>
            </g>
          );
        })}
 
        {/* ── Courbes ── */}
        {series.map((s, i) => (
          <g key={i}>
            <path d={path(s.data)} fill="none" stroke={s.color} strokeWidth="1.8" />
            {s.data.length > 0 && (
              <circle
                cx={xScale(s.data.length - 1)}
                cy={yScale(s.data[s.data.length - 1] ?? 0)}
                r="3"
                fill={s.color}
              />
            )}
          </g>
        ))}
 
        {/* ── Labels X (3 dates réparties) ── */}
        {days.length > 0 &&
          [0, Math.floor(days.length / 2), days.length - 1].map((idx, i) => (
            <text
              key={i}
              x={xScale(idx)}
              y={H - 8}
              textAnchor="middle"
              fontSize="9"
              fontFamily="DM Mono,monospace"
              fill="#5a6a7a"
            >
              J{days[idx]}
            </text>
          ))}
 
        {/* ── Indicateurs au survol ── */}
        {hover && hover.idx != null && (
          <g>
            {/* Ligne verticale */}
            <line
              x1={xScale(hover.idx)}
              y1={PAD.t}
              x2={xScale(hover.idx)}
              y2={H - PAD.b}
              stroke="#4fc3f7"
              strokeWidth="0.8"
              strokeDasharray="3,3"
              opacity="0.6"
            />
            {/* Points sur chaque courbe à l'index survolé */}
            {series.map((s, i) => {
              const v = s.data[hover.idx];
              if (v == null) return null;
              return (
                <circle
                  key={i}
                  cx={xScale(hover.idx)}
                  cy={yScale(v)}
                  r="4"
                  fill={s.color}
                  stroke="#0e1115"
                  strokeWidth="1.5"
                />
              );
            })}
          </g>
        )}
      </svg>
 
      {/* ── Tooltip HTML (positionné absolu) ── */}
      {hover && hover.idx != null && (
        <div
          style={{
            position: "absolute",
            // Positionne le tooltip à gauche ou à droite selon la position
            // pour qu'il ne sorte pas du panneau
            left:
              hover.idx > days.length / 2
                ? `${(xScale(hover.idx) / W) * 100 - 16}%`
                : `${(xScale(hover.idx) / W) * 100 + 2}%`,
            transform:
              hover.idx > days.length / 2 ? "translateX(-100%)" : "none",
            top: "8px",
            background: "rgba(14, 17, 21, 0.95)",
            border: "1px solid #1e2530",
            borderRadius: "6px",
            padding: ".5rem .65rem",
            fontFamily: "DM Mono,monospace",
            fontSize: ".65rem",
            color: "#e8edf2",
            pointerEvents: "none",
            zIndex: 10,
            minWidth: "120px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
          }}
        >
          <div
            style={{
              color: "#8a9aaa",
              fontSize: ".58rem",
              textTransform: "uppercase",
              letterSpacing: ".06em",
              marginBottom: ".35rem",
              paddingBottom: ".3rem",
              borderBottom: "1px solid #1e2530",
            }}
          >
            {formatLabel(hover.idx)}
          </div>
          {series.map((s, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: ".6rem",
                marginTop: i === 0 ? 0 : ".2rem",
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: ".35rem",
                  color: "#8a9aaa",
                }}
              >
                <span
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "2px",
                    background: s.color,
                  }}
                />
                {s.name || `Série ${i + 1}`}
              </span>
              <strong style={{ color: s.color, fontWeight: 700 }}>
                {fmt(s.data[hover.idx])}
              </strong>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
 

 
function MiniSpark({ data, color, height = 36, labels = null }) {
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);
 
  if (!data || data.length === 0) return null;
 
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const W = 100;
 
  const pointAt = (i) => {
    const x = (i / Math.max(data.length - 1, 1)) * W;
    const y = height - ((data[i] - min) / range) * (height - 4) - 2;
    return [x, y];
  };
 
  const points = data.map((_, i) => pointAt(i));
  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p[0]} ${p[1]}`)
    .join(" ");
  const fillPath = linePath + ` L ${W} ${height} L 0 ${height} Z`;
 
  const handleMouseMove = (e) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const ratio = W / rect.width;
    const mouseX = (e.clientX - rect.left) * ratio;
    const t = Math.max(0, Math.min(1, mouseX / W));
    const idx = Math.round(t * (data.length - 1));
    setHover(idx);
  };
 
  const formatLabel = (idx) => {
    if (labels && labels[idx]) {
      try {
        const d = new Date(labels[idx]);
        return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
      } catch {
        return labels[idx];
      }
    }
    return `J-${data.length - 1 - idx}`;
  };
 
  const fmt = (v) => {
    if (v == null) return "—";
    if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
    return Math.round(v).toString();
  };
 
  return (
    <div style={{ position: "relative" }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${height}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height: `${height}px`, display: "block", cursor: "crosshair" }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHover(null)}
      >
        <path d={fillPath} fill={color} opacity="0.12" />
        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle
          cx={points[points.length - 1][0]}
          cy={points[points.length - 1][1]}
          r="2.2"
          fill={color}
        />
        {hover != null && data[hover] != null && (
          <>
            <line
              x1={points[hover][0]}
              y1="0"
              x2={points[hover][0]}
              y2={height}
              stroke={color}
              strokeWidth="0.6"
              opacity="0.5"
              strokeDasharray="2,2"
            />
            <circle
              cx={points[hover][0]}
              cy={points[hover][1]}
              r="3"
              fill={color}
              stroke="#0e1115"
              strokeWidth="1"
            />
          </>
        )}
      </svg>
 
      {hover != null && data[hover] != null && (
        <div
          style={{
            position: "absolute",
            bottom: `${height + 4}px`,
            left: `${(points[hover][0] / W) * 100}%`,
            transform:
              hover > data.length / 2 ? "translateX(-100%)" : "translateX(0)",
            background: "rgba(14, 17, 21, 0.95)",
            border: "1px solid #1e2530",
            borderRadius: "4px",
            padding: ".25rem .5rem",
            fontFamily: "DM Mono,monospace",
            fontSize: ".58rem",
            color: "#e8edf2",
            pointerEvents: "none",
            zIndex: 10,
            whiteSpace: "nowrap",
            boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
          }}
        >
          <span style={{ color: "#8a9aaa", marginRight: ".4rem" }}>
            {formatLabel(hover)}
          </span>
          <strong style={{ color, fontWeight: 700 }}>{fmt(data[hover])}</strong>
        </div>
      )}
    </div>
  );
}

function Donut({ segments }) {
  const R = 50, INNER = 32, CX = 60, CY = 60;
  let acc = 0;
  return (
    <DonutSvg viewBox="0 0 120 120">
      {segments.map((s, i) => {
        const start = acc, end = acc + s.pct; acc = end;
        const a1 = (start/100)*2*Math.PI - Math.PI/2;
        const a2 = (end/100)*2*Math.PI - Math.PI/2;
        const x1 = CX + R*Math.cos(a1), y1 = CY + R*Math.sin(a1);
        const x2 = CX + R*Math.cos(a2), y2 = CY + R*Math.sin(a2);
        const ix1 = CX + INNER*Math.cos(a2), iy1 = CY + INNER*Math.sin(a2);
        const ix2 = CX + INNER*Math.cos(a1), iy2 = CY + INNER*Math.sin(a1);
        const large = s.pct > 50 ? 1 : 0;
        return <path key={i} d={`M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} L ${ix1} ${iy1} A ${INNER} ${INNER} 0 ${large} 0 ${ix2} ${iy2} Z`} fill={s.c}/>;
      })}
    </DonutSvg>
  );
}

function PageHeatmap() {
  return (
    <svg viewBox="0 0 320 220" preserveAspectRatio="xMidYMid meet" style={{width:"100%",height:"auto",maxHeight:"240px",display:"block"}}>
      <rect x="2" y="2" width="316" height="216" rx="8" fill="#0f1419" stroke="#1a2530" strokeWidth="0.5"/>
      <rect x="12" y="10" width="160" height="14" rx="3" fill="#161d24"/>
      <rect x="180" y="10" width="50" height="14" rx="3" fill="#161d24"/>
      <rect x="238" y="10" width="70" height="14" rx="3" fill="#161d24"/>
      <rect x="12" y="32" width="200" height="84" rx="5" fill="#161d24" stroke="#1a2530" strokeWidth="0.5"/>
      <polyline points="20,100 40,84 60,90 80,68 100,60 120,52 140,58 160,44 180,38 200,46" fill="none" stroke="#4fc3f7" strokeWidth="1.2" opacity="0.7"/>
      <rect x="12" y="122" width="200" height="20" rx="4" fill="#161d24" stroke="#1a2530" strokeWidth="0.5"/>
      <rect x="12" y="148" width="200" height="20" rx="4" fill="#161d24" stroke="#1a2530" strokeWidth="0.5"/>
      <text x="20" y="135" fontSize="8" fontFamily="DM Mono" fill="#8a9aaa">Oui — 72%</text>
      <text x="20" y="161" fontSize="8" fontFamily="DM Mono" fill="#8a9aaa">Non — 28%</text>
      <rect x="220" y="32" width="90" height="84" rx="5" fill="#161d24" stroke="#1a2530" strokeWidth="0.5"/>
      <rect x="228" y="42" width="74" height="22" rx="4" fill="rgba(0,230,118,.15)" stroke="#00e676" strokeWidth="0.5"/>
      <text x="265" y="57" textAnchor="middle" fontSize="10" fontWeight="700" fill="#00e676">BUY</text>
      <rect x="228" y="72" width="74" height="22" rx="4" fill="rgba(255,59,92,.12)" stroke="#ff3b5c" strokeWidth="0.5"/>
      <text x="265" y="87" textAnchor="middle" fontSize="10" fontWeight="700" fill="#ff3b5c">SELL</text>
      <rect x="220" y="122" width="90" height="46" rx="5" fill="#161d24" stroke="#1a2530" strokeWidth="0.5"/>
      <text x="265" y="148" textAnchor="middle" fontSize="8" fontFamily="DM Mono" fill="#5a6a7a">Historique</text>
      <rect x="12" y="176" width="298" height="32" rx="4" fill="#161d24" stroke="#1a2530" strokeWidth="0.5"/>
      <text x="20" y="193" fontSize="8" fontFamily="DM Mono" fill="#5a6a7a">💬 Comments</text>

      <circle cx="265" cy="53" r="22" fill="#ff3b5c" opacity="0.55"/>
      <text x="265" y="56" textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff">38%</text>
      <circle cx="105" cy="132" r="16" fill="#ff9800" opacity="0.5"/>
      <text x="105" y="135" textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff">22%</text>
      <circle cx="110" cy="74" r="18" fill="#ffc107" opacity="0.45"/>
      <text x="110" y="77" textAnchor="middle" fontSize="8" fontWeight="700" fill="#000">16%</text>
      <circle cx="265" cy="83" r="13" fill="#ff9800" opacity="0.55"/>
      <text x="265" y="86" textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff">11%</text>
      <circle cx="105" cy="158" r="11" fill="#ffc107" opacity="0.4"/>
      <text x="105" y="161" textAnchor="middle" fontSize="7" fontWeight="700" fill="#000">8%</text>
      <circle cx="265" cy="146" r="7" fill="#4fc3f7" opacity="0.5"/>
      <text x="265" y="148" textAnchor="middle" fontSize="6" fontWeight="700" fill="#fff">2%</text>
      <circle cx="60" cy="192" r="8" fill="#4fc3f7" opacity="0.4"/>
      <text x="60" y="194" textAnchor="middle" fontSize="6" fontWeight="700" fill="#fff">3%</text>
    </svg>
  );
}

function FunnelEvolutionCard({ step, index }) {
  const daily = step.daily || [];

  // Variation : moyenne de la 2e moitié vs 1ère moitié
  const half = Math.floor(daily.length / 2);
  const firstHalf  = daily.slice(0, half);
  const secondHalf = daily.slice(half);
  const avgFirst   = firstHalf.length ? firstHalf.reduce((a,b)=>a+b,0) / firstHalf.length : 0;
  const avgSecond  = secondHalf.length ? secondHalf.reduce((a,b)=>a+b,0) / secondHalf.length : 0;
  const deltaPct   = avgFirst > 0 ? ((avgSecond - avgFirst) / avgFirst) * 100 : 0;
  const isUp       = deltaPct >= 0;

  // Couleur selon la conversion (cohérent avec le funnel principal)
  const c = step.from
    ? (step.conversion >= 80 ? "green" : step.conversion >= 50 ? "gold" : "red")
    : "accent";
  const lineColor = c === "green" ? "#00e676" : c === "gold" ? "#ffc107" : c === "red" ? "#ff3b5c" : "#4fc3f7";

  return (
    <EvolCard $c={c} $i={index}>
      <EvolHead>
        <EvolLabel>{step.step}</EvolLabel>
        {!isNaN(deltaPct) && Math.abs(deltaPct) > 0.5 && (
          <EvolDelta $up={isUp}>
            {isUp ? "▲" : "▼"} {Math.abs(deltaPct).toFixed(0)}%
          </EvolDelta>
        )}
      </EvolHead>
      <EvolValue>{fmtNum(step.n)}</EvolValue>
      <MiniSpark data={daily} color={lineColor}/>
    </EvolCard>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════════════ */
export default function UXAnalyticsDashboard() {
  const [range, setRange] = useState("30d");
  const [refreshKey, setRefreshKey] = useState(0);

  // Parallel data fetches — each section has its own loading + mock fallback
  const ov   = useApiSection(() => adminGetUXOverview(range),   MOCK_OVERVIEW,   [range, refreshKey]);
  const fun  = useApiSection(() => adminGetUXFunnel(range),     MOCK_FUNNEL,     [range, refreshKey]);
  const clk  = useApiSection(() => adminGetUXClicks(range),     MOCK_CLICKS,     [range, refreshKey]);
  const act  = useApiSection(() => adminGetUXActivation(range), MOCK_ACTIVATION, [range, refreshKey]);
  const eng  = useApiSection(() => adminGetUXEngagement(range), MOCK_ENGAGEMENT, [range, refreshKey]);
  const coh  = useApiSection(() => adminGetUXCohorts(),         MOCK_COHORTS,    [refreshKey]);
  const err  = useApiSection(() => adminGetUXErrors(range),     MOCK_ERRORS,     [range, refreshKey]);
  const lat  = useApiSection(() => adminGetUXLatency(range),    MOCK_LATENCY,    [range, refreshKey]);
  const dev  = useApiSection(() => adminGetUXDevices(range),    MOCK_DEVICES,    [range, refreshKey]);
  const beh  = useApiSection(() => adminGetUXBehavioral(range), MOCK_BEHAVIORAL, [range, refreshKey]);
  const vol = useApiSection(() => adminGetUXVolume(range), MOCK_VOLUME, [range, refreshKey]);
const cty = useApiSection(() => adminGetUXByCountry(range), MOCK_BY_COUNTRY, [range, refreshKey]);  

  const maxClicks = useMemo(() => {
    if (!clk.data?.by_page?.length) return 1;
    return Math.max(...clk.data.by_page.map(p => p.clicks));
  }, [clk.data]);

  const maxElements = useMemo(() => {
    if (!clk.data?.top_elements?.length) return 1;
    return Math.max(...clk.data.top_elements.map(e => e.count));
  }, [clk.data]);

  return (
    <Wrap>
      <Head>
        <div>
          <Title>UX <span>Analytics</span></Title>
          <Sub>Comportement utilisateur —EVPTrading. Mesures de l'engagement, friction et psychologie du trader.</Sub>
        </div>
        <div style={{display:"flex",gap:".5rem",alignItems:"center"}}>
          <RangeTabs>
            {RANGES.map(r => (
              <RangeBtn key={r.val} $a={range===r.val} onClick={()=>setRange(r.val)}>{r.label}</RangeBtn>
            ))}
          </RangeTabs>
          <RefreshBtn onClick={()=>setRefreshKey(k=>k+1)} title="Rafraîchir">↻</RefreshBtn>
        </div>
      </Head>

      {/* ════════ KPI STRIP ════════ */}
      {ov.loading ? (
        <KpiGrid>{[1,2,3,4,5].map(i => <PanelSkel key={i} $h="100px"/>)}</KpiGrid>
      ) : (
        <KpiGrid>
          <Kpi $c="green" $i={0}>
            <KpiLabel>🔥 Stickiness DAU/MAU</KpiLabel>
            <KpiVal>{ov.data.stickiness}%</KpiVal>
            <KpiSub>{ov.isMock && "données démo"}</KpiSub>
          </Kpi>
          <Kpi $c="gold" $i={1}>
            <KpiLabel>⏱ Time to first trade</KpiLabel>
            <KpiVal>{ov.data.ttft_median_min} min</KpiVal>
            <KpiSub>médiane</KpiSub>
          </Kpi>
          <Kpi $c="red" $i={2}>
            <KpiLabel>🎯 Signup → 1er trade</KpiLabel>
            <KpiVal>{ov.data.signup_to_trade}%</KpiVal>
            <KpiSub>conversion</KpiSub>
          </Kpi>
          <Kpi $c="accent" $i={3}>
            <KpiLabel>⚡ Latence trade (p95)</KpiLabel>
            <KpiVal>{ov.data.latency_p95_ms} ms</KpiVal>
            <KpiSub>{ov.data.latency_p95_ms < 500 ? "✓ sain" : "à optimiser"}</KpiSub>
          </Kpi>
          <Kpi $c="green" $i={4}>
            <KpiLabel>📊 Sessions / jour</KpiLabel>
            <KpiVal>{fmtNum(ov.data.sessions_today)}</KpiVal>
            <KpiSub $ok>aujourd'hui</KpiSub>
          </Kpi>
        </KpiGrid>
      )}

{/* ════════ FUNNEL ════════ */}
      <Panel>
        {fun.isMock && <MockTag>démo</MockTag>}
        <PanelHead>
          <div>
            <PanelTitle>Funnel d'acquisition</PanelTitle>
            <PanelHint style={{display:"block",marginTop:".2rem"}}>VISITE → 1ER TRADE</PanelHint>
          </div>
          {fun.data && fun.data.steps.length > 1 && (
            <PanelHint>
              Conversion globale : {Math.round(fun.data.steps[fun.data.steps.length-1].n / fun.data.steps[0].n * 100)}%
            </PanelHint>
          )}
        </PanelHead>

        {/* Bars existantes */}
        {fun.loading ? <PanelSkel $h="240px"/> : fun.data.steps.map((step, i) => {
          const widthPct = (step.n / fun.data.steps[0].n) * 100;
          const c = step.from ? conversionColor(step.conversion) : "green";
          return (
            <FunRow key={i}>
              <FunLabel>{step.step}</FunLabel>
              <FunTrack><FunFill $pct={widthPct} $c={c}/></FunTrack>
              <FunNum>{fmtNum(step.n)}</FunNum>
              <FunPct $c={c}>{step.from ? `${step.conversion}%` : "—"}</FunPct>
            </FunRow>
          );
        })}

        {/* ── NOUVEAU : Courbes d'évolution par étape ── */}
        {!fun.loading && fun.data.steps.some(s => s.daily?.length) && (
          <>
            <div style={{
              marginTop: "1.5rem",
              paddingTop: "1rem",
              borderTop: "1px solid #1e2530",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
              <PanelTitle style={{fontSize: ".82rem"}}>Évolution par étape</PanelTitle>
              <PanelHint>Quotidien · 30 derniers jours</PanelHint>
            </div>
            <EvolGrid>
              {fun.data.steps.map((step, i) => (
                <FunnelEvolutionCard key={i} step={step} index={i}/>
              ))}
            </EvolGrid>
          </>
        )}
      </Panel>

    
{/* ════════ TRADE VOLUME ════════ */}
<Panel style={{marginTop: "1.25rem"}}>
  {vol.isMock && <MockTag>démo</MockTag>}
  <PanelHead>
    <div>
      <PanelTitle>Volume de trade par jour</PanelTitle>
      <PanelHint style={{display:"block",marginTop:".2rem"}}>
        Volume EVP & nombre de trades quotidiens
      </PanelHint>
    </div>
    {!vol.loading && (
      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:".15rem"}}>
        <PanelHint>
          Total&nbsp;: <strong style={{color:"#e8edf2"}}>{fmtNum(vol.data.total_volume)} EVP</strong>
        </PanelHint>
        <PanelHint>
          Moyenne&nbsp;: <strong style={{color:"#e8edf2"}}>{fmtNum(vol.data.avg_per_day)} EVP / jour</strong>
        </PanelHint>
      </div>
    )}
  </PanelHead>
 
  {vol.loading ? <PanelSkel/> : (
    <>
      <div style={{display:"flex",gap:"1rem",marginBottom:".5rem",fontSize:".7rem"}}>
        <span style={{color:"#8a9aaa",display:"flex",alignItems:"center",gap:".3rem"}}>
          <span style={{width:"10px",height:"2px",background:"#00e676"}}/>Volume (EVP)
        </span>
        <span style={{color:"#8a9aaa",display:"flex",alignItems:"center",gap:".3rem"}}>
          <span style={{width:"10px",height:"2px",background:"#4fc3f7"}}/>Nombre de trades
        </span>
      </div>
      <Sparkline
        days={vol.data.days}
        series={[
          { name:"Volume", data: vol.data.volume, color: "#00e676" },
          { name:"Trades", data: vol.data.trades, color: "#4fc3f7" },
        ]}
      />
    </>
  )}
</Panel>

      {/* ════════ CLICKS BY PAGE & TOP ELEMENTS ════════ */}
{/* ════════ CLICKS BY PAGE & TOP ELEMENTS ════════ */}
      {(() => {
        // Merge avec le mock champ par champ
        const merged = clk.loading
          ? { data: { by_page: [], top_elements: [] }, missing: [] }
          : mergeWithMock(clk.data, MOCK_CLICKS);

        const byPageIsMock      = clk.isMock || merged.missing.includes("by_page");
        const topElementsIsMock = clk.isMock || merged.missing.includes("top_elements");

        const dataByPage      = merged.data.by_page;
        const dataTopElements = merged.data.top_elements;

        const maxClicksLocal = Math.max(...dataByPage.map(p => p.clicks), 1);
        const maxElementsLocal = Math.max(...dataTopElements.map(e => e.count), 1);

        return (
          <Row2 style={{marginTop:"1.25rem"}}>
            <Panel>
              {byPageIsMock && <MockTag>démo</MockTag>}
              <PanelHead>
                <PanelTitle>
                  Clics par page
                  {byPageIsMock && <FieldMockBadge>données démo</FieldMockBadge>}
                </PanelTitle>
                <PanelHint>+ taux de rebond</PanelHint>
              </PanelHead>
              {clk.loading ? <PanelSkel/> : dataByPage.map(p => (
                <BarItem key={p.page}>
                  <BarIcon>{ICON_BY_PAGE[p.page] || "📄"}</BarIcon>
                  <BarLabel>{p.page}</BarLabel>
                  <BarTrack><BarFill $pct={(p.clicks/maxClicksLocal)*100} $c="accent"/></BarTrack>
                  <BarVal>{fmtNum(p.clicks)}</BarVal>
                  <span style={{width:"40px",textAlign:"right",fontFamily:"DM Mono,monospace",
                    fontSize:".62rem",
                    color:p.bounce>40?"#ff3b5c":p.bounce>25?"#ffc107":"#5a6a7a"}}>
                    {p.bounce}%
                  </span>
                </BarItem>
              ))}
            </Panel>

            <Panel>
              {topElementsIsMock && <MockTag>démo</MockTag>}
              <PanelHead>
                <PanelTitle>
                  Top éléments cliqués
                  {topElementsIsMock && <FieldMockBadge>données démo</FieldMockBadge>}
                </PanelTitle>
                <PanelHint>Site-wide</PanelHint>
              </PanelHead>
              {clk.loading ? <PanelSkel/> : dataTopElements.map((e, i) => (
                <BarItem key={i}>
                  <BarIcon>{ICON_BY_ELEMENT(e.element)}</BarIcon>
                  <BarLabel>{e.element}</BarLabel>
                  <BarTrack>
                    <BarFill $pct={(e.count/maxElementsLocal)*100} $c={COLOR_BY_ELEMENT(e.element)}/>
                  </BarTrack>
                  <BarVal>{fmtNum(e.count)}</BarVal>
                </BarItem>
              ))}
              {topElementsIsMock && (
                <Insight $c="gold" style={{marginTop:".8rem"}}>
                  <span>💡</span>
                  <span>
                    Pour avoir les vraies données : ajouter <code>data-track="element_name"</code>{" "}
                    sur les boutons importants côté frontend et logger les clics dans <code>UXClickEvent</code> côté backend.
                  </span>
                </Insight>
              )}
            </Panel>
          </Row2>
        );
      })()}
      {/* ════════ USERS BY COUNTRY ════════ */}
      <Panel>
   {false && <WorldSessionsMap data={cty.data.countries || []} />}

</Panel>
<Panel style={{marginTop: "1.25rem"}}>
  {cty.isMock && <MockTag>démo</MockTag>}
  <PanelHead>
    <div>
      <PanelTitle>Inscrits par pays</PanelTitle>
      <PanelHint style={{display:"block",marginTop:".2rem"}}>
        Répartition géographique · Volume EVP & trades par pays
      </PanelHint>
    </div>
    {!cty.loading && (
      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:".15rem"}}>
        <PanelHint>
          <strong style={{color:"#e8edf2"}}>{cty.data.country_count}</strong> pays ·{" "}
          <strong style={{color:"#e8edf2"}}>{fmtNum(cty.data.total_users)}</strong> inscrits
        </PanelHint>
        <PanelHint>
          Volume total&nbsp;: <strong style={{color:"#00e676"}}>{fmtNum(cty.data.total_volume)} EVP</strong>
        </PanelHint>
      </div>
    )}
  </PanelHead>
 
  {cty.loading ? <PanelSkel $h="280px"/> : cty.data.countries.length === 0 ? (
    <p style={{fontSize:".8rem",color:"#5a6a7a",padding:"1.5rem 0",textAlign:"center"}}>
      Aucune donnée pays sur la période.
    </p>
  ) : (() => {
    const maxUsers = Math.max(...cty.data.countries.map(c => c.users_count), 1);
 
    return (
      <div style={{display:"flex",flexDirection:"column",gap:".2rem"}}>
        {/* Header de colonnes */}
        <div style={{
          display:"grid",
          gridTemplateColumns:"32px 1fr 110px 1fr 90px 70px",
          gap:".75rem",
          padding:".4rem .25rem",
          borderBottom:"1px solid #1e2530",
          fontFamily:"DM Mono,monospace",
          fontSize:".58rem",
          color:"#5a6a7a",
          textTransform:"uppercase",
          letterSpacing:".08em",
        }}>
          <span/>
          <span>Pays</span>
          <span style={{textAlign:"right"}}>Inscrits</span>
          <span>Distribution</span>
          <span style={{textAlign:"right"}}>Volume EVP</span>
          <span style={{textAlign:"right"}}>Trades</span>
        </div>
 
        {/* Lignes pays */}
        {cty.data.countries.map((c, i) => {
          const pct = (c.users_count / maxUsers) * 100;
          const color = i === 0 ? "#00e676"
                      : i === 1 ? "#4fc3f7"
                      : i === 2 ? "#ffc107"
                      : "#5a6a7a";
          return (
            <div key={c.code} style={{
              display:"grid",
              gridTemplateColumns:"32px 1fr 110px 1fr 90px 70px",
              gap:".75rem",
              padding:".55rem .25rem",
              alignItems:"center",
              borderBottom:"1px solid rgba(30,37,48,.4)",
              fontSize:".78rem",
              transition:"background .15s",
            }}>
              <span style={{fontSize:"1.15rem",lineHeight:1}}>{c.flag}</span>
 
              <span style={{
                display:"flex",
                alignItems:"center",
                gap:".4rem",
                color:"#e8edf2",
                overflow:"hidden",
                textOverflow:"ellipsis",
                whiteSpace:"nowrap",
              }}>
                {c.name}
                <span style={{
                  fontFamily:"DM Mono,monospace",
                  fontSize:".58rem",
                  color:"#5a6a7a",
                  padding:".05rem .35rem",
                  background:"#0e1115",
                  border:"1px solid #1e2530",
                  borderRadius:"3px",
                  letterSpacing:".04em",
                }}>
                  {c.code}
                </span>
              </span>
 
              <span style={{
                textAlign:"right",
                fontFamily:"DM Mono,monospace",
                fontSize:".82rem",
                fontWeight:600,
                color: i < 3 ? color : "#e8edf2",
              }}>
                {fmtNum(c.users_count)}
              </span>
 
              <div style={{
                background:"#0e1115",
                height:"6px",
                borderRadius:"3px",
                overflow:"hidden",
                position:"relative",
              }}>
                <div style={{
                  height:"100%",
                  width:`${pct}%`,
                  background:`linear-gradient(90deg, ${color}, ${color}88)`,
                  borderRadius:"3px",
                  transition:"width .4s cubic-bezier(.4,0,.2,1)",
                }}/>
              </div>
 
              <span style={{
                textAlign:"right",
                fontFamily:"DM Mono,monospace",
                fontSize:".72rem",
                color: c.total_volume > 0 ? "#00e676" : "#5a6a7a",
              }}>
                {c.total_volume > 0 ? fmtNum(c.total_volume) : "—"}
              </span>
 
              <span style={{
                textAlign:"right",
                fontFamily:"DM Mono,monospace",
                fontSize:".72rem",
                color: c.trades_count > 0 ? "#4fc3f7" : "#5a6a7a",
              }}>
                {c.trades_count > 0 ? fmtNum(c.trades_count) : "—"}
              </span>
            </div>
          );
        })}
      </div>
    );
  })()}
 
  {/* Insight si peu de pays */}
  {!cty.loading && cty.data.country_count > 0 && cty.data.country_count < 5 && (
    <Insight $c="accent" style={{marginTop:"1rem"}}>
      <span>🌍</span>
      <span>
        Présence dans <strong>{cty.data.country_count}</strong> pays seulement —
        opportunité d'expansion géographique via campagnes localisées.
      </span>
    </Insight>
  )}
</Panel>
 
      {/* ════════ ACTIVATION ════════ */}
      <Row2>
        <Panel>
          {act.isMock && <MockTag>démo</MockTag>}
          <PanelHead>
            <PanelTitle>Time to first trade</PanelTitle>
            <PanelHint>Distribution</PanelHint>
          </PanelHead>
          {act.loading ? <PanelSkel $h="170px"/> : (
            <HistoWrap>
              {act.data.ttft.map(b => (
                <HistoBar key={b.bucket}>
                  <HistoVal>{b.pct}%</HistoVal>
                  <HistoFill $h={b.pct*2.5} $c={b.pct >= 30 ? "warn" : "accent"}/>
                  <HistoLab>{b.bucket}</HistoLab>
                </HistoBar>
              ))}
            </HistoWrap>
          )}
        </Panel>

        <Panel>
          {act.isMock && <MockTag>démo</MockTag>}
          <PanelHead>
            <PanelTitle>Taille moyenne du trade</PanelTitle>
            <PanelHint>EVP</PanelHint>
          </PanelHead>
          {act.loading ? <PanelSkel $h="170px"/> : (
            <HistoWrap>
              {act.data.trade_size.map(b => (
                <HistoBar key={b.bucket}>
                  <HistoVal>{b.pct}%</HistoVal>
                  <HistoFill $h={b.pct*2.3} $c={b.bucket.startsWith(">") || b.bucket === "1-10" ? "warn" : "accent"}/>
                  <HistoLab>{b.bucket}</HistoLab>
                </HistoBar>
              ))}
            </HistoWrap>
          )}
        </Panel>
      </Row2>

      {/* ════════ RETENTION ════════ */}
      <Row2>
        <Panel>
          {eng.isMock && <MockTag>démo</MockTag>}
          <PanelHead>
            <PanelTitle>DAU vs MAU</PanelTitle>
            <PanelHint>Stickiness {ov.data?.stickiness ?? 0}%</PanelHint>
          </PanelHead>
          {eng.loading ? <PanelSkel/> : (
            <>
              <div style={{display:"flex",gap:"1rem",marginBottom:".5rem",fontSize:".7rem"}}>
                <span style={{color:"#8a9aaa",display:"flex",alignItems:"center",gap:".3rem"}}>
                  <span style={{width:"10px",height:"2px",background:"#00e676"}}/>DAU
                </span>
                <span style={{color:"#8a9aaa",display:"flex",alignItems:"center",gap:".3rem"}}>
                  <span style={{width:"10px",height:"2px",background:"#4fc3f7"}}/>MAU
                </span>
              </div>
              <Sparkline
                days={eng.data.days}
                series={[
                  { name:"DAU", data:eng.data.dau, color:"#00e676" },
                  { name:"MAU", data:eng.data.mau, color:"#4fc3f7" },
                ]}
              />
            </>
          )}
        </Panel>

        <Panel>
          {coh.isMock && <MockTag>démo</MockTag>}
          <PanelHead>
            <PanelTitle>Rétention par cohorte</PanelTitle>
            <PanelHint>% actif après N semaines</PanelHint>
          </PanelHead>
          {coh.loading ? <PanelSkel/> : (
            <CohortTable>
              <thead>
                <tr>
                  <CohortHead style={{textAlign:"left"}}>Cohorte</CohortHead>
                  {["W0","W1","W2","W3","W4","W5","W6","W7"].map(w => <CohortHead key={w}>{w}</CohortHead>)}
                </tr>
              </thead>
              <tbody>
                {coh.data.cohorts.map(c => (
                  <tr key={c.cohort}>
                    <CohortLabel>{c.cohort}</CohortLabel>
                    {c.values.map((v, i) => (
                      <CohortCell key={i} $v={v}>{v != null ? `${v}%` : ""}</CohortCell>
                    ))}
                  </tr>
                ))}
              </tbody>
            </CohortTable>
          )}
        </Panel>
      </Row2>

      {/* ════════ FRICTION ════════ */}
      <Row2>
        <Panel>
          <PanelHead>
            <PanelTitle>Heatmap d'interaction</PanelTitle>
            <PanelHint>MarketDetail — illustrative</PanelHint>
          </PanelHead>
          <PageHeatmap/>
          <Insight $c="accent">
            <span>ℹ</span>
            <span>Pour piloter cette heatmap par les vraies données : exporter le top-éléments par page depuis l'endpoint <code>ux/clicks/</code> et mapper sur les coordonnées des éléments.</span>
          </Insight>
        </Panel>

        <Panel>
          {err.isMock && <MockTag>démo</MockTag>}
          <PanelHead>
            <PanelTitle>Top erreurs utilisateur</PanelTitle>
            <PanelHint>Messages d'erreur</PanelHint>
          </PanelHead>
          {err.loading ? <PanelSkel/> : err.data.errors.length === 0 ? (
            <p style={{fontSize:".75rem",color:"#5a6a7a",padding:"1rem 0"}}>Aucune erreur enregistrée sur la période.</p>
          ) : (
            err.data.errors.map((e, i) => (
              <BarItem key={i}>
                <BarIcon>⚠</BarIcon>
                <BarLabel>{e.label}</BarLabel>
                <BarTrack><BarFill $pct={e.pct*2.2} $c={e.pct > 30 ? "red" : e.pct > 15 ? "gold" : "accent"}/></BarTrack>
                <BarVal>{e.pct}%</BarVal>
              </BarItem>
            ))
          )}
        </Panel>
      </Row2>

      {/* ════════ LATENCY & DEVICE ════════ */}
      <Row2>
        <Panel>
          {lat.isMock && <MockTag>démo</MockTag>}
          <PanelHead>
            <PanelTitle>Latence d'exécution</PanelTitle>
            <PanelHint>p50 / p95</PanelHint>
          </PanelHead>
          {lat.loading ? <PanelSkel/> : (
            <>
              <div style={{display:"flex",gap:"1rem",marginBottom:".5rem",fontSize:".7rem"}}>
                <span style={{color:"#8a9aaa",display:"flex",alignItems:"center",gap:".3rem"}}>
                  <span style={{width:"10px",height:"2px",background:"#00e676"}}/>p50
                </span>
                <span style={{color:"#8a9aaa",display:"flex",alignItems:"center",gap:".3rem"}}>
                  <span style={{width:"10px",height:"2px",background:"#ff3b5c"}}/>p95
                </span>
              </div>
              <Sparkline
                days={lat.data.days}
                series={[
                  { name:"p50", data:lat.data.p50, color:"#00e676" },
                  { name:"p95", data:lat.data.p95, color:"#ff3b5c" },
                ]}
              />
            </>
          )}
        </Panel>

        <Panel>
          {dev.isMock && <MockTag>démo</MockTag>}
          <PanelHead>
            <PanelTitle>Mobile vs Desktop</PanelTitle>
            <PanelHint>Répartition des sessions</PanelHint>
          </PanelHead>
          {dev.loading ? <PanelSkel $h="160px"/> : (
            <DonutWrap>
              <Donut segments={dev.data.devices}/>
              <LegendList>
                {dev.data.devices.map(d => (
                  <LegItem key={d.name}>
                    <LegDot $c={d.c}/>
                    {d.name}
                    <LegPct>{d.pct}%</LegPct>
                  </LegItem>
                ))}
              </LegendList>
            </DonutWrap>
          )}
        </Panel>
      </Row2>

      {/* ════════ BEHAVIORAL KPIs ════════ */}
      <Panel>
        {beh.isMock && <MockTag>démo</MockTag>}
        <PanelHead>
          <div>
            <PanelTitle>Psychologie du trader — KPIs comportementaux</PanelTitle>
            <PanelHint style={{display:"block",marginTop:".2rem"}}>Signaux qualitatifs sur la confiance et la décision</PanelHint>
          </div>
        </PanelHead>
        {beh.loading ? <PanelSkel/> : (
          <StatList>
            {beh.data.items.map((s, i) => (
              <StatRow key={i}>
                <StatLbl>{s.lbl}</StatLbl>
                <StatVl $c={s.c}>{s.val}</StatVl>
              </StatRow>
            ))}
          </StatList>
        )}
      </Panel>
    </Wrap>
  );
}
